/**
 * Scene 5: «Ночные огни (крупно)» — a narrow 12° view of a night street (D-017). At 96 px per degree the
 * halos of multifocal lenses (≈0.35°), cataract glare and starburst streaks are large enough to see on any screen.
 */

import { ellipseDeg, fillRectDeg, geom, hash01, polygonDeg, rectDeg, roundRectPath, textDeg } from './draw-utils';
import { NIGHT_TONE, RENDER_H, RENDER_W, type Scene, type SceneLight } from './types';

const HFOV = 12;
const G = geom(HFOV);
const BOTTOM = -G.vfov / 2;
const HORIZON = -0.55;
const VP: readonly [number, number] = [0.35, HORIZON];

const WHITE: [number, number, number] = [0.9, 0.95, 1.0];
const WARM: [number, number, number] = [1.0, 0.85, 0.6];
const RED: [number, number, number] = [1.0, 0.16, 0.1];

/** Street lamps on the right: head centre (deg), head radius (deg), pole foot y (deg). */
const LAMPS: ReadonlyArray<readonly [x: number, y: number, r: number, foot: number]> = [
  [2.3, 2.6, 0.13, BOTTOM],
  [3.5, 1.65, 0.1, -1.9],
  [4.35, 1.05, 0.075, -1.25],
  [5.0, 0.62, 0.055, -0.95],
];

function drawBlocks(ctx: CanvasRenderingContext2D): void {
  const blocks: ReadonlyArray<readonly [number, number, number]> = [
    [-6, -4.9, 2.4], [-4.9, -3.6, 1.6], [-3.6, -2.7, 1.1], [-2.7, -1.9, 0.7],
    [1.2, 1.9, 0.8], [1.9, 2.9, 1.3], [2.9, 4.2, 1.9], [4.2, 6, 2.6],
  ];
  blocks.forEach(([x0, x1, h], i) => {
    fillRectDeg(ctx, G, x0, x1, HORIZON - 0.05, HORIZON + h, i % 2 ? '#15151a' : '#18171b');
    const cols = Math.max(1, Math.floor((x1 - x0) / 0.22));
    const rows = Math.max(1, Math.floor(h / 0.28));
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (hash01(i * 131 + r * 17 + c) < 0.62) continue;
        const wx = x0 + 0.06 + c * ((x1 - x0 - 0.08) / cols);
        const wy = HORIZON + 0.12 + r * (h / rows);
        fillRectDeg(ctx, G, wx, wx + 0.1, wy, wy + 0.13, '#b08a4a');
      }
    }
  });
}

function drawRoad(ctx: CanvasRenderingContext2D): void {
  polygonDeg(ctx, G, [[-6, BOTTOM], [6, BOTTOM], [VP[0] + 0.15, VP[1]], [VP[0] - 0.15, VP[1]]]);
  ctx.fillStyle = '#17181b';
  ctx.fill();
  const xb = -2.2;
  for (let k = 0; k < 12; k++) {
    const t0 = 0.05 + k * 0.08;
    const t1 = t0 + 0.04;
    const x0 = VP[0] + (xb - VP[0]) * t0;
    const y0 = VP[1] + (BOTTOM - VP[1]) * t0;
    const x1 = VP[0] + (xb - VP[0]) * t1;
    const y1 = VP[1] + (BOTTOM - VP[1]) * t1;
    const w0 = 0.01 + t0 * 0.05;
    const w1 = 0.01 + t1 * 0.05;
    polygonDeg(ctx, G, [[x0 - w0, y0], [x0 + w0, y0], [x1 + w1, y1], [x1 - w1, y1]]);
    ctx.fillStyle = '#3a3a36';
    ctx.fill();
  }
}

function drawTrafficLight(ctx: CanvasRenderingContext2D): void {
  fillRectDeg(ctx, G, -4.64, -4.56, -1.2, 1.4, '#2a2b2e');
  const box = rectDeg(G, -4.8, -4.4, 1.35, 2.45);
  roundRectPath(ctx, box.x, box.y, box.w, box.h, 6);
  ctx.fillStyle = '#0d0e10';
  ctx.fill();
  const lamps: ReadonlyArray<readonly [number, string]> = [[2.15, '#5a1712'], [1.85, '#2a2210'], [1.55, '#0f2414']];
  for (const [y, colour] of lamps) {
    ctx.fillStyle = colour;
    ellipseDeg(ctx, G, -4.6, y, 0.11, 0.11);
    ctx.fill();
  }
}

function drawLamps(ctx: CanvasRenderingContext2D): void {
  ctx.strokeStyle = '#2c2d31';
  ctx.lineCap = 'round';
  for (const [x, y, r, foot] of LAMPS) {
    ctx.lineWidth = Math.max(2, r * 0.5 * G.ppd);
    ctx.beginPath();
    ctx.moveTo(G.X(x + r * 3), G.Y(foot));
    ctx.lineTo(G.X(x + r * 3), G.Y(y + r * 0.8));
    ctx.lineTo(G.X(x), G.Y(y + r * 0.8));
    ctx.stroke();
    ctx.fillStyle = '#6a604c';
    ellipseDeg(ctx, G, x, y, r * 1.2, r * 0.7);
    ctx.fill();
  }
}

function drawCars(ctx: CanvasRenderingContext2D): void {
  // Oncoming car at ~60 m: headlights 1.2° apart.
  const car = rectDeg(G, -2.55, -0.65, -2.0, -1.25);
  roundRectPath(ctx, car.x, car.y, car.w, car.h, 0.15 * G.ppd);
  ctx.fillStyle = '#121418';
  ctx.fill();
  fillRectDeg(ctx, G, -2.25, -0.95, -1.55, -1.3, '#0a0c0f');
  ctx.fillStyle = '#c8c6bc';
  ellipseDeg(ctx, G, -2.2, -1.78, 0.1, 0.065);
  ctx.fill();
  ellipseDeg(ctx, G, -1.0, -1.78, 0.1, 0.065);
  ctx.fill();
  // A second car further away, ~150 m.
  fillRectDeg(ctx, G, -0.85, -0.1, -1.1, -0.78, '#131519');
  ctx.fillStyle = '#c8c6bc';
  ellipseDeg(ctx, G, -0.72, -1.0, 0.045, 0.03);
  ctx.fill();
  ellipseDeg(ctx, G, -0.23, -1.0, 0.045, 0.03);
  ctx.fill();
}

function drawFar(ctx: CanvasRenderingContext2D): void {
  const sky = ctx.createLinearGradient(0, 0, 0, G.Y(HORIZON));
  sky.addColorStop(0, '#05070e');
  sky.addColorStop(1, '#141a28');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, RENDER_W, RENDER_H);
  fillRectDeg(ctx, G, -6, 6, BOTTOM, HORIZON, '#101114');
  drawBlocks(ctx);
  drawRoad(ctx);
  drawTrafficLight(ctx);
  drawLamps(ctx);
  drawCars(ctx);
  // Pharmacy sign board on the left facade; its lettering is emissive.
  fillRectDeg(ctx, G, -5.9, -3.6, 0.55, 1.05, '#1c1d1f');
}

/** Neon lettering, added on top with an HDR scale (Uzbek street signage in every language, I18N). */
function drawNeon(ctx: CanvasRenderingContext2D): void {
  ctx.fillStyle = '#3ddc73';
  const cross = rectDeg(G, -5.8, -5.5, 0.64, 0.96);
  ctx.fillRect(cross.x + cross.w * 0.35, cross.y, cross.w * 0.3, cross.h);
  ctx.fillRect(cross.x, cross.y + cross.h * 0.35, cross.w, cross.h * 0.3);
  textDeg(ctx, G, 'DORIXONA', -5.35, 0.68, 0.22, '#3ddc73', 800);
}

function lights(): SceneLight[] {
  const out: SceneLight[] = [
    { layer: 'far', x: -2.2, y: -1.78, rx: 0.1, ry: 0.065, color: WHITE, intensity: 400 },
    { layer: 'far', x: -1.0, y: -1.78, rx: 0.1, ry: 0.065, color: WHITE, intensity: 400 },
    { layer: 'far', x: -0.72, y: -1.0, rx: 0.045, ry: 0.03, color: WHITE, intensity: 400 },
    { layer: 'far', x: -0.23, y: -1.0, rx: 0.045, ry: 0.03, color: WHITE, intensity: 400 },
    { layer: 'far', x: -4.6, y: 2.15, rx: 0.1, ry: 0.1, color: RED, intensity: 120 },
  ];
  for (const [x, y, r] of LAMPS) {
    out.push({ layer: 'far', x, y: y - r * 0.1, rx: r, ry: r * 0.55, color: WARM, intensity: 200 });
  }
  return out;
}

export const nightLightsScene: Scene = {
  id: 'night_lights',
  hfov: HFOV,
  lighting: 'night',
  exposure: 1.0,
  tone: NIGHT_TONE,
  layers: [{ id: 'far', V: 0.02, draw: (ctx) => drawFar(ctx), emissive: (ctx) => drawNeon(ctx), emissiveScale: 5 }],
  lights: () => lights(),
  tags: [{ x: -1.6, y: -2.45, key: 'tag.m60' }],
};
